/**
 * 创建一个单例 Promise 函数
 */
export function createSingletonPromise<Args extends unknown[], T>(fn: (...args: Args) => Promise<T>) {
  let cachedPromise: null | Promise<T> = null;

  return (...args: Args) => {
    if (!cachedPromise) {
      cachedPromise = fn(...args).finally(() => {
        cachedPromise = null; // 清空缓存，允许下次调用重新创建 Promise
      });
    }

    return cachedPromise;
  };
}

export function retryService<Args extends unknown[], T>(
  fn: (...args: Args) => Promise<T>,
  options?: {
    retryCount?: number;
    customVerify?: (res: T) => boolean;
    sleepTime?: number;
    sleepTimeStep?: (retryCount: number) => number;
    onError?: () => void;
  },
) {
  const { retryCount = 3, customVerify, sleepTimeStep, onError } = options || {};
  let currentRetryCount = 0;

  const callFn = async function retry(...args: Args) {
    try {
      currentRetryCount += 1;

      const res = await fn(...args);

      /** 如果自定义校验失败，抛出返回信息 */
      if (customVerify && !customVerify(res)) {
        throw res;
      }

      return res;
    } catch (error) {
      onError?.();
      if (currentRetryCount < retryCount) {
        const sleepTime = sleepTimeStep ? sleepTimeStep(currentRetryCount) : options?.sleepTime || 0;
        // callFn.context.sleepTimes.push(sleepTime);
        if (sleepTime) {
          await sleep(sleepTime);
        }
        return retry(...args);
      } else {
        throw error;
      }
    }
  };

  // callFn.context = {
  //   currentRetryCount: 0,
  //   sleepTimes: [] as number[],
  // };

  return callFn;
}

/** 总能保证 promise 成功 */
export async function promiseSuccessWrap<T>(promise: Promise<T>, defaultValue?: T) {
  try {
    const res = await promise;
    return res;
  } catch (err) {
    console.error(err);
    return defaultValue;
  }
}

export const sleep = (time = 500) => new Promise((resolve) => setTimeout(resolve, time));

export interface DeferredPromise<T> {
  promise: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (value?: Error) => void;
}

export const createDeferredPromise = <T>(): DeferredPromise<T> => {
  let resolve: (value: T | PromiseLike<T>) => void = () => undefined;
  let reject: (value?: Error) => void = () => undefined;

  const promise = new Promise<T>((r, _reject) => {
    resolve = r;
    reject = _reject;
  });

  return {
    promise,
    resolve,
    reject,
  };
};

export const createPromiseWithTimeout = <T>(
  promiseConstructor: (resolve: (v: T) => void, reject: (reason?: any) => void) => void,
  {
    timeout = 10000,
    defaultValue,
  }: {
    timeout?: number;
    /** 如果有则 resolve，否则 reject */
    defaultValue?: T;
  } = {},
) => {
  return new Promise<T>((resolve, reject) => {
    promiseConstructor(resolve, reject);

    setTimeout(() => {
      if (defaultValue) {
        resolve(defaultValue);
      } else {
        reject(new Error('timed out'));
      }
    }, timeout);
  });
};
